/**
 * 🔐 SECURE FULCRUM API SERVICE
 *
 * This service handles Fulcrum API interactions with secure API key management.
 * API keys are stored encrypted in the backend database per user and available via UserContext.
 */

import { ApiEndpoint, Entity, FulcrumLayerConfig, GeoJsonLayerConfig } from "../types";
import { getDataSourceType } from "./adapters";

/**
 * Fulcrum API base URLs for different regions
 */
export const FULCRUM_BASE_URLS = {
  US: "https://api.fulcrumapp.com/api/v2",
  AU: "https://api.fulcrumapp-au.com/api/v2",
  EU: "https://api.fulcrumapp-eu.com/api/v2"
};

export type FulcrumRegion = keyof typeof FULCRUM_BASE_URLS;

// Note: Region-specific URLs are dynamically selected from FULCRUM_BASE_URLS
// based on the user's profile setting (fulcrum_region)

// Try to dynamically import fulcrum-app if available
let fulcrumClient: any = null;

// This will be set to true if we successfully import and initialize the fulcrum-app client
let usingFulcrumLib = false;

// Try to initialize the Fulcrum client
try {
  // This will only work if the fulcrum-app package is installed
  // We'll use dynamic import when actually needed
  console.log("Fulcrum API service initialized - using UserContext for API keys");
} catch (err) {
  console.warn("Fulcrum client library not available, using direct API calls");
}

// Photo caching
interface CachedPhoto {
  blobUrl: string;
  expiry: number;
}

const photoCache = new Map<string, CachedPhoto>();
const PHOTO_CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const MAX_CACHE_SIZE = 100; // Prevent memory leaks with reasonable limit

/**
 * Clean up expired cache entries and enforce size limits
 * MEMORY LEAK FIX: Prevents unlimited cache growth
 */
const cleanupPhotoCache = () => {
  const now = Date.now();
  let removedCount = 0;

  // First, remove expired entries
  for (const [url, cached] of photoCache.entries()) {
    if (cached.expiry < now) {
      URL.revokeObjectURL(cached.blobUrl);
      photoCache.delete(url);
      removedCount++;
    }
  }

  // If still over size limit, remove oldest entries (LRU-style)
  if (photoCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(photoCache.entries());
    entries.sort(([, a], [, b]) => a.expiry - b.expiry); // oldest first

    const toRemove = entries.slice(0, photoCache.size - MAX_CACHE_SIZE);
    for (const [url, cached] of toRemove) {
      URL.revokeObjectURL(cached.blobUrl);
      photoCache.delete(url);
      removedCount++;
    }
  }

  void removedCount;
};

/**
 * Gets API key from UserContext only - no environment variable fallback
 * This function should be called with the user profile from UserContext
 */
const getApiKeyFromProfile = (userProfile: any): string | null => {
  // Handle null/undefined userProfile gracefully (e.g., during logout)
  if (!userProfile) {
    return null;
  }

  if (userProfile.fulcrum_api_key) {
    return userProfile.fulcrum_api_key;
  }

  // Check nested user object for API key
  if (userProfile.user?.fulcrum_api_key) {
    return userProfile.user.fulcrum_api_key;
  }

  console.warn("No Fulcrum API key available. Please configure your API key in user settings.");
  return null;
};

/**
 * Detect Fulcrum region from photo ID or form ID length
 * AU region uses longer UUIDs, US uses shorter ones
 */
export const detectFulcrumRegion = (id?: string): FulcrumRegion => {
  if (!id) return "AU"; // Default to AU
  return id.length > 30 ? "AU" : "US";
};

/**
 * Gets Fulcrum region from UserContext only - no environment variable fallback
 * This function should be called with the user profile from UserContext
 */
const getRegionFromProfile = (userProfile: any): FulcrumRegion => {
  // Handle null/undefined userProfile gracefully (e.g., during logout)
  if (!userProfile) {
    return "AU"; // Default region when no profile
  }

  // Get region from user profile, default to AU if not configured
  if (userProfile.fulcrum_region) {
    return userProfile.fulcrum_region;
  }

  // Check nested user object for region
  if (userProfile.user?.fulcrum_region) {
    return userProfile.user.fulcrum_region;
  }

  // Default to AU region if not configured in user settings
  console.warn("No Fulcrum region configured in user settings, defaulting to AU region");
  return "AU";
};

/**
 * Fetches a single Fulcrum form by ID
 * @param formId The Fulcrum form ID to fetch
 * @param userProfile The user profile from UserContext containing the API key and region
 */
export const fetchFulcrumForm = async (formId: string, userProfile: any) => {
  try {
    console.log(`Fetching Fulcrum form ${formId}`);
    const apiKey = getApiKeyFromProfile(userProfile);
    const region = getRegionFromProfile(userProfile);

    if (!apiKey) {
      throw new Error("No Fulcrum API key available. Please configure your API key in user settings.");
    }

    // Get the base URL for the user's region
    const baseUrl = FULCRUM_BASE_URLS[region];

    // First try with fulcrum-app library if available
    if (usingFulcrumLib && fulcrumClient) {
      try {
        console.log(`Using fulcrum-app library to fetch form ${formId}`);
        return await fulcrumClient.forms.find(formId);
      } catch (libError) {
        console.warn("Error using fulcrum-app library:", libError);
        console.log("Falling back to direct API calls");
      }
    }

    // Direct API call with user's API key using correct region
    console.log(`Fetching Fulcrum form ${formId} via direct API (region: ${region})`);
    const response = await fetch(`${baseUrl}/forms/${formId}`, {
      headers: {
        Accept: "application/json",
        "X-ApiToken": apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Fulcrum API error (${response.status}): ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching Fulcrum form:", error);
    throw error;
  }
};

/**
 * Fetches all forms in the account
 * @param userProfile The user profile from UserContext containing the API key and region
 */
export const fetchFulcrumForms = async (userProfile: any) => {
  try {
    console.log("Fetching Fulcrum forms");
    const apiKey = getApiKeyFromProfile(userProfile);
    const region = getRegionFromProfile(userProfile);

    if (!apiKey) {
      throw new Error("No Fulcrum API key available. Please configure your API key in user settings.");
    }

    // Get the base URL for the user's region
    const baseUrl = FULCRUM_BASE_URLS[region];

    // First try with fulcrum-app library if available
    if (usingFulcrumLib && fulcrumClient) {
      try {
        console.log("Using fulcrum-app library to fetch forms");
        return await fulcrumClient.forms.all();
      } catch (libError) {
        console.warn("Error using fulcrum-app library:", libError);
        console.log("Falling back to direct API calls");
      }
    }

    // Direct API call with user's API key
    console.log("Fetching Fulcrum forms via direct API");
    const response = await fetch(`${baseUrl}/forms`, {
      headers: {
        Accept: "application/json",
        "X-ApiToken": apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Fulcrum API error (${response.status}): ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching Fulcrum forms:", error);
    throw error;
  }
};

/**
 * Fetches records from a form as GeoJSON
 * @param formId The Fulcrum form ID to fetch records from
 * @param userProfile The user profile from UserContext containing the API key and region
 * @param queryParams Additional query parameters to filter results
 */
export const fetchFulcrumRecordsAsGeoJSON = async (formId: string, userProfile: any, queryParams: Record<string, any> = {}) => {
  try {
    // Early return if no user profile (e.g., during logout)
    if (!userProfile) {
      console.log(`[FulcrumService] No user profile available, returning empty GeoJSON for form ${formId}`);
      return {
        type: "FeatureCollection",
        features: []
      };
    }

    console.log(`Fetching Fulcrum records for form ${formId}`);
    const apiKey = getApiKeyFromProfile(userProfile);
    const region = getRegionFromProfile(userProfile);

    if (!apiKey) {
      console.log(`[FulcrumService] No API key available for form ${formId}, returning empty GeoJSON`);
      return {
        type: "FeatureCollection",
        features: []
      };
    }

    // Get the base URL for the user's region
    const baseUrl = FULCRUM_BASE_URLS[region];

    // First try with fulcrum-app library if it's available
    if (usingFulcrumLib && fulcrumClient) {
      try {
        console.log("Using fulcrum-app library to fetch records as GeoJSON");
        // Implementation would go here if we were using the client library
      } catch (libError) {
        console.warn("Error using fulcrum-app library:", libError);
        console.log("Falling back to direct API calls");
      }
    }

    // Direct API call with user's API key
    console.log("Fetching Fulcrum records via direct API");

    // Combine form_id with other query params
    const allParams: Record<string, any> = { form_id: formId, ...queryParams };

    // Build query params
    const queryString = Object.keys(allParams)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(allParams[key])}`)
      .join("&");

    // Make the request
    console.log(`Fetching from ${baseUrl}/records.geojson?${queryString}`);
    const response = await fetch(`${baseUrl}/records.geojson?${queryString}`, {
      headers: {
        Accept: "application/json",
        "X-ApiToken": apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Fulcrum API error (${response.status}): ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Successfully fetched ${data?.features?.length || 0} records`);
    return data;
  } catch (error: any) {
    console.error("Error fetching Fulcrum records as GeoJSON:", error);

    // For API unavailability, return empty GeoJSON instead of crashing
    if (
      error?.message?.includes("Failed to fetch") ||
      error?.message?.includes("503") ||
      error?.message?.includes("CORS") ||
      error?.message?.includes("Access-Control-Allow-Origin")
    ) {
      console.warn("Fulcrum API unavailable, returning empty GeoJSON");
      return {
        type: "FeatureCollection",
        features: []
      };
    }

    throw error;
  }
};

/**
 * Fetches a single page of records from a Fulcrum form with pagination metadata.
 *
 * Unlike the GeoJSON endpoint, this function uses the standard JSON API (/records.json)
 * which supports pagination. It automatically handles calculating next/previous page
 * URLs if they are missing from the response and wraps the result in a structure
 * compatible with standard paginated data adapters.
 *
 * @param formId - The unique identifier of the Fulcrum form.
 * @param userProfile - The user profile object (from UserContext) containing the `fulcrum_api_key` and `fulcrum_region`.
 * @param queryParams - Optional filtering and pagination parameters (e.g., `{ page: 1, per_page: 50 }`).
 * @param onProgress - Optional callback for tracking progress (useful for consumers tracking single-page fetch status).
 * @returns A promise that resolves to an array containing a single object with the record results and pagination links.
 */
export const fetchFulcrumPaginatedRecords = async (
  formId: string,
  userProfile: any,
  queryParams: Record<string, any> = {},
  onProgress?: (currentPage: number, totalPages: number, accumulatedCount: number) => void
): Promise<any[]> => {
  // Early return if no user profile (e.g. during logout)
  if (!userProfile) {
    console.log(`[FulcrumService] No user profile available, returning empty records for form ${formId}`);
    return [];
  }

  const apiKey = getApiKeyFromProfile(userProfile);
  const region = getRegionFromProfile(userProfile);

  if (!apiKey) {
    console.log(`[FulcrumService] No API key available for form ${formId}, returning empty records`);
    return [];
  }

  const baseUrl = FULCRUM_BASE_URLS[region];

  const allParams = { form_id: formId, ...queryParams };
  const stringifiedParams = Object.entries(allParams)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");

  try {
    console.log(`[FulcrumService] Fetching records for form ${formId}`);
    console.log(`[FulcrumService] GET ${baseUrl}/records.json?${stringifiedParams}`);

    const response = await fetch(`${baseUrl}/records.json?${stringifiedParams}`, {
      headers: {
        Accept: "application/json",
        "X-ApiToken": apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Fulcrum API error (${response.status}): ${response.statusText}`);
    }

    const data = await response.json();

    // Helper to extract or dynamically construct the next page URL
    const getNextPageUrl = (data: any) => {
      if (data?.next_page_url) return data.next_page_url;
      if (data?.current_page < data?.total_pages) {
        const params = { ...allParams, page: data.current_page + 1 };
        const qs = Object.entries(params)
          .filter(([_, value]) => value !== undefined && value !== null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&");
        return `${baseUrl}/records.json?${qs}`;
      }
      return null;
    };

    // Helper to extract or dynamically construct the previous page URL
    const getPreviousPageUrl = (data: any) => {
      if (data?.prev_page_url) return data.prev_page_url;
      if (data?.current_page > 1) {
        const params = { ...allParams, page: data.current_page - 1 };
        const qs = Object.entries(params)
          .filter(([_, value]) => value !== undefined && value !== null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&");
        return `${baseUrl}/records.json?${qs}`;
      }
      return null;
    };

    const paginatedData = [
      {
        results: data?.records || [],
        count: data?.total_count,
        next: getNextPageUrl(data),
        previous: getPreviousPageUrl(data)
      }
    ];

    return paginatedData;
  } catch (error: any) {
    console.error(`[FulcrumService] Error during paginated fetch for form ${formId}:`, error);

    // For network / CORS / service-unavailable errors return whatever we managed to accumulate
    if (
      error?.message?.includes("Failed to fetch") ||
      error?.message?.includes("503") ||
      error?.message?.includes("CORS") ||
      error?.message?.includes("Access-Control-Allow-Origin")
    ) {
      console.warn("Fulcrum API unavailable, returning empty JSON");
      return [];
    }

    throw error;
  }
};

/**
 * Create a valid GeoJsonLayerConfig from a fulcrum layer
 */
export const createGeoJsonLayerConfig = (layer: any, geoJsonData: any): GeoJsonLayerConfig => {
  return {
    id: layer.id,
    type: "geojson",
    metadata: { isFulcrum: true },
    url: geoJsonData,
    geometry: layer.geometry || "symbol",
    defaultIcon: layer.defaultIcon || "icons/fulcrum-marker.png",
    initialVisibility: layer.initialVisibility !== false,
    geostyler: layer.geostyler,
    style: layer.style || {
      iconSize: 0.3,
      labelField: "{_title}",
      labelSize: 10,
      labelColor: "#ffffff"
    },
    options: {
      minZoom: 0,
      maxZoom: 22
    }
  };
};

/**
 * Fulcrum specific configuration options
 */
export interface FulcrumConfig {
  queryParams?: Record<string, string>;
  region?: FulcrumRegion;
}

/**
 * Check if a layer config has a 'fulcrumConfig' property
 */
export const hasFulcrumConfig = (layer: any): layer is { fulcrumConfig: FulcrumConfig } => {
  return layer && typeof layer === "object" && "fulcrumConfig" in layer;
};

/**
 * Check if a layer is a Fulcrum layer
 */
export const isFulcrumLayer = (layer: any): boolean => {
  return layer && typeof layer === "object" && layer.type === "fulcrum";
};

/**
 * Layer configs that still represent Fulcrum-sourced data after the preload step in
 * `useMapLibreLayers` (which swaps `type: "fulcrum"` for `type: "geojson"` + `metadata.isFulcrum`).
 * Both branches carry `defaultIcon`, `style`, etc. — safe to read in the legend panel.
 */
export type FulcrumDerivedLayerConfig = FulcrumLayerConfig | (GeoJsonLayerConfig & { metadata: { isFulcrum: true } });

/**
 * Type guard for {@link FulcrumDerivedLayerConfig}. Prefer this over `type === "fulcrum"` when
 * reading layers from the store; raw `type === "fulcrum"` is only valid before preload.
 */
export const isFulcrumDerivedLayerConfig = (
  layer: { type?: string; metadata?: { isFulcrum?: boolean } } | null | undefined
): layer is FulcrumDerivedLayerConfig => {
  if (!layer || typeof layer !== "object") return false;
  return layer.type === "fulcrum" || (layer.type === "geojson" && !!layer.metadata?.isFulcrum);
};

/**
 * Fetches a single Fulcrum record by ID
 *
 * @param recordId The ID of the Fulcrum record to fetch
 * @param userProfile The user profile from UserContext containing the API key and region
 */
export const fetchFulcrumRecordById = async (recordId: string, userProfile: any) => {
  try {
    // Early return if no user profile (e.g., during logout)
    if (!userProfile) {
      return null;
    }

    const apiKey = getApiKeyFromProfile(userProfile);
    const region = getRegionFromProfile(userProfile);

    if (!apiKey) {
      return null;
    }

    // Get the base URL for the user's region
    const baseUrl = FULCRUM_BASE_URLS[region];

    // First try with fulcrum-app library if it's available
    if (usingFulcrumLib && fulcrumClient) {
      try {
        // Implementation would go here if we were using the client library
      } catch (libError) {
        console.warn("Error using fulcrum-app library:", libError);
      }
    }

    // Direct API call
    const response = await fetch(`${baseUrl}/records/${recordId}.json`, {
      headers: {
        Accept: "application/json",
        "X-ApiToken": apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Fulcrum API error (${response.status}): ${response.statusText}`);
    }

    const responseData = await response.json();

    // The Fulcrum API returns the record in a 'record' property
    if (!responseData.record) {
      throw new Error("Invalid Fulcrum API response format");
    }

    return responseData.record;
  } catch (error: any) {
    console.error(`Error fetching Fulcrum record ${recordId}:`, error);

    // For API unavailability, return null instead of crashing
    if (
      error?.message?.includes("Failed to fetch") ||
      error?.message?.includes("503") ||
      error?.message?.includes("CORS") ||
      error?.message?.includes("Access-Control-Allow-Origin")
    ) {
      console.warn(`Fulcrum API unavailable for record ${recordId}, returning null`);
      return null;
    }

    throw error;
  }
};

/**
 * Generate Fulcrum photo URL
 *
 * @param photoId The Fulcrum photo ID
 * @param region The Fulcrum region (US, AU, EU)
 * @param thumbnail Whether to get thumbnail (default) or full size
 */
export const getFulcrumPhotoUrl = (photoId: string, region: FulcrumRegion = "AU", thumbnail: boolean = true): string => {
  const baseUrl = FULCRUM_BASE_URLS[region];
  const endpoint = thumbnail ? `/photos/${photoId}/thumbnail.jpg` : `/photos/${photoId}.jpg`;
  return `${baseUrl}${endpoint}`;
};

/**
 * Check if a URL is a Fulcrum photo URL that needs authentication
 */
export const isFulcrumPhotoUrl = (url: string): boolean => {
  return url.includes("api.fulcrumapp") && url.includes("/photos/");
};

/**
 * Fetch a Fulcrum photo with authentication and return a blob URL for display
 *
 * @param photoId The Fulcrum photo ID
 * @param userProfile The user profile from UserContext containing the API key and region
 * @param thumbnail Whether to get thumbnail (default) or full size
 */
export const getFulcrumPhotoAsBlob = async (
  photoId: string,
  userProfile: any,
  thumbnail: boolean = true,
  overrideRegion?: FulcrumRegion
): Promise<string> => {
  const region = overrideRegion || getRegionFromProfile(userProfile);
  const photoUrl = getFulcrumPhotoUrl(photoId, region, thumbnail);

  // Clean up old cache entries
  cleanupPhotoCache();

  // Check if we already have this photo cached
  const cached = photoCache.get(photoUrl);
  if (cached && cached.expiry > Date.now()) {
    return cached.blobUrl;
  }

  try {
    const apiKey = getApiKeyFromProfile(userProfile);

    if (!apiKey) {
      throw new Error("No Fulcrum API key available for photo access");
    }

    const response = await fetch(photoUrl, {
      method: "GET",
      headers: {
        "X-ApiToken": apiKey,
        Accept: "image/*"
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch photo: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Cache the blob URL with proactive cleanup
    photoCache.set(photoUrl, {
      blobUrl,
      expiry: Date.now() + PHOTO_CACHE_DURATION
    });

    // Trigger cleanup if cache is getting large (prevents memory buildup)
    if (photoCache.size > MAX_CACHE_SIZE * 0.8) {
      cleanupPhotoCache();
    }

    return blobUrl;
  } catch (error) {
    console.error(`[FulcrumService] Error fetching photo ${photoId}:`, error);

    // Check if this is a 403 error that should be handled by the fallback system
    const errorMessage = error instanceof Error ? error.message : String(error);
    const is403Error =
      errorMessage.includes("403") || errorMessage.includes("Forbidden") || errorMessage.toLowerCase().includes("forbidden");

    if (is403Error) {
      console.log(`[FulcrumService] 403 error detected, re-throwing for fallback handling`);
      // Re-throw 403 errors so they can be handled by authMediaAdapters fallback logic
      throw error;
    }

    // For other errors, return placeholder
    console.log(`[FulcrumService] Non-403 error, returning placeholder`);
    return "/icons/image-placeholder.svg";
  }
};

/**
 * Cleanup function to revoke all blob URLs (call on app unmount)
 */
export const cleanupAllPhotoBlobs = () => {
  for (const [, cached] of photoCache.entries()) {
    URL.revokeObjectURL(cached.blobUrl);
  }
  photoCache.clear();
  console.log("[FulcrumService] Cleaned up all photo blob URLs");
};

/**
 * Fetches reverse relationships for a target record using Fulcrum Query API
 *
 * This function finds all records in source forms that have record link fields pointing to the target record.
 *
 * @param targetRecordId The ID of the record to find reverse relationships for
 * @param reverseRelationships Array of reverse relationship configurations from config.json
 * @param userProfile The user profile from UserContext containing the API key and region
 * @returns Promise<Array<any>> Array of records that link to the target record
 */
export const fetchReverseRelationships = async (
  targetRecordId: string,
  reverseRelationships: any[],
  userProfile: any,
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any[]> => {
  try {
    console.log(`🔄 Fetching reverse relationships for record ${targetRecordId}`);
    const apiKey = getApiKeyFromProfile(userProfile);
    const region = getRegionFromProfile(userProfile);

    if (!apiKey) {
      throw new Error("No Fulcrum API key available. Please configure your API key in user settings.");
    }

    if (!reverseRelationships || reverseRelationships.length === 0) {
      console.log("🔄 No reverse relationships configured for this endpoint");
      return [];
    }

    // Get the base URL for the user's region
    const baseUrl = FULCRUM_BASE_URLS[region];
    const allResults: any[] = [];

    // Process each reverse relationship configuration
    for (let i = 0; i < reverseRelationships.length; i++) {
      const reverseRel = reverseRelationships[i];
      try {
        console.log(`🔄 Querying ${reverseRel.sourceFormName} for relationships to ${targetRecordId}`);

        // Report progress
        if (onProgress) {
          onProgress(`Querying ${reverseRel.sourceFormName}`, i + 1, reverseRelationships.length);
        }

        // Build SQL query to find records that link to our target record
        // Query API uses individual columns for each form field, not form_values JSON
        // We need to select the specific record link field and _record_id

        // Primary query approach - search arrays using ANY() function
        const sql = `
          SELECT _record_id, ${reverseRel.recordLinkFieldId}
          FROM "${reverseRel.sourceFormId}" 
          WHERE '${targetRecordId}' = ANY(${reverseRel.recordLinkFieldId})
        `;

        console.log(`🔄 Executing SQL: ${sql.trim()}`);

        // Execute the query using Fulcrum Query API with timeout
        const queryPromise = fetch(`${baseUrl}/query`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-ApiToken": apiKey
          },
          body: JSON.stringify({
            q: sql.trim()
          })
        });

        // Add 5-second timeout per query
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Query timeout for ${reverseRel.sourceFormName} (5 seconds)`));
          }, 5000);
        });

        let response = (await Promise.race([queryPromise, timeoutPromise])) as Response;

        if (!response.ok) {
          // Get error details for better debugging
          let errorDetails = `${response.status} ${response.statusText}`;
          try {
            const errorResponse = await response.text();
            console.warn(`🔄 Query error details for ${reverseRel.sourceFormName}:`, errorResponse);
            errorDetails += ` - ${errorResponse}`;
          } catch (e) {
            console.warn(`🔄 Could not parse error response for ${reverseRel.sourceFormName}`);
          }

          console.warn(`🔄 Query failed for ${reverseRel.sourceFormName}: ${errorDetails}`);

          // Try alternative query approach if the first one fails
          console.log(`🔄 Attempting alternative query approach for ${reverseRel.sourceFormName}...`);

          const alternativeSQL = `
             SELECT _record_id, ${reverseRel.recordLinkFieldId}
             FROM "${reverseRel.sourceFormId}" 
             WHERE ${reverseRel.recordLinkFieldId}::text LIKE '%${targetRecordId}%'
           `;

          console.log(`🔄 Executing alternative SQL: ${alternativeSQL.trim()}`);

          const alternativeQueryPromise = fetch(`${baseUrl}/query`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-ApiToken": apiKey
            },
            body: JSON.stringify({
              q: alternativeSQL.trim()
            })
          });

          // Add 5-second timeout for alternative query too
          const alternativeTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error(`Alternative query timeout for ${reverseRel.sourceFormName} (5 seconds)`));
            }, 5000);
          });

          const alternativeResponse = (await Promise.race([alternativeQueryPromise, alternativeTimeoutPromise])) as Response;

          if (!alternativeResponse.ok) {
            console.warn(
              `🔄 Alternative query also failed for ${reverseRel.sourceFormName}: ${alternativeResponse.status} ${alternativeResponse.statusText}`
            );
            continue; // Skip this relationship and continue with others
          }

          // Use the alternative response if it succeeded
          response = alternativeResponse;
        }

        // Parse CSV response from Fulcrum Query API
        const csvText = await response.text();
        console.log(`🔄 Raw CSV response for ${reverseRel.sourceFormName}:`, csvText.substring(0, 200) + "...");

        const lines = csvText.trim().split("\n");
        if (lines.length < 2) {
          console.log(`🔄 No data rows found for ${reverseRel.sourceFormName}`);
          continue;
        }

        const headers = lines[0].split(",");
        const rows = lines.slice(1).map((line) => {
          const values = line.split(",");
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index];
          });
          return row;
        });

        const queryResult = { rows };

        if (queryResult.rows && queryResult.rows.length > 0) {
          console.log(`🔄 Found ${queryResult.rows.length} ${reverseRel.sourceFormName} records linking to ${targetRecordId}`);

          // For Query API results, we need to fetch full record details via REST API
          // since Query API only gives us the _record_id and relationship field
          const recordIds = queryResult.rows.map((row: any) => row._record_id);

          console.log(`🔄 Fetching full record details for ${recordIds.length} records from ${reverseRel.sourceFormName}`);

          // Fetch full record details for each found record using REST API
          const fullRecords = await Promise.all(
            recordIds.map(async (recordId: string) => {
              try {
                const recordResponse = await fetch(`${baseUrl.replace("/query", "")}/records/${recordId}.json`, {
                  headers: {
                    Accept: "application/json",
                    "X-ApiToken": apiKey
                  }
                });

                if (recordResponse.ok) {
                  const recordData = await recordResponse.json();
                  return recordData.record;
                } else {
                  console.warn(`Failed to fetch full record details for ${recordId}: ${recordResponse.status}`);
                  return null;
                }
              } catch (error) {
                console.warn(`Error fetching record ${recordId}:`, error);
                return null;
              }
            })
          );

          // Filter out failed fetches and transform results with unified name extraction
          const transformedResults = await Promise.all(
            fullRecords
              .filter((record) => record !== null)
              .map(async (record: any) => {
                let recordName = "Unknown Record";
                let recordDescription = "";
                let recordThumbnail = null;

                console.log(`🔍 [FULCRUM-REV-REL] Starting name extraction for record:`, {
                  recordId: record.id,
                  formId: record.form_id,
                  hasFormValues: !!record.form_values,
                  formValuesKeys: record.form_values ? Object.keys(record.form_values) : []
                });

                if (record.form_values) {
                  try {
                    // Get the form schema for proper field mapping
                    const formSchema = await fetchFulcrumForm(record.form_id, userProfile);
                    const { mapFormFields, extractUnifiedDisplayName, extractUnifiedDisplayDescription, DEFAULT_FULCRUM_MAPPING } =
                      await import("./adapters/SharedAdapterUtils");
                    const transformedFields = mapFormFields(record.form_values, formSchema?.form || formSchema);

                    // Use unified extraction helpers with default mapping context
                    recordName = extractUnifiedDisplayName({
                      formValues: transformedFields,
                      rawItem: record,
                      fieldMapping: DEFAULT_FULCRUM_MAPPING,
                      userProfile,
                      formSchema
                    });

                    recordDescription = extractUnifiedDisplayDescription({
                      formValues: transformedFields,
                      rawItem: record,
                      fieldMapping: DEFAULT_FULCRUM_MAPPING,
                      userProfile,
                      formSchema
                    });

                    // Extract thumbnail by scanning ALL form_values for any photo array field
                    // (Fulcrum photo fields use arbitrary field IDs, not a hardcoded "photos" key)
                    const photoEntry = Object.values(record.form_values).find(
                      (v): v is any[] => Array.isArray(v) && v.length > 0 && (v[0] as any)?.photo_id
                    );
                    if (photoEntry) {
                      const region = (userProfile?.fulcrum_region as FulcrumRegion) || "AU";
                      const baseUrl = FULCRUM_BASE_URLS[region];
                      recordThumbnail = `${baseUrl}/photos/${photoEntry?.[0]?.photo_id}/thumbnail.jpg`;
                    }
                  } catch (error) {
                    console.warn(`Error extracting data from record ${record.id}:`, error);
                  }
                }

                // Fallback name if still unknown
                if (recordName === "Unknown Record" || recordName.startsWith("Record ")) {
                  recordName = `${reverseRel.sourceFormName} ${record.id.substring(0, 8)}`;
                }

                console.log(`🔍 [FULCRUM-REV-REL] Completed name extraction:`, {
                  recordId: record.id,
                  extractedName: recordName,
                  extractedDescription: recordDescription,
                  extractedThumbnail: recordThumbnail,
                  sourceFormName: reverseRel.sourceFormName
                });

                return {
                  id: record.id,
                  form_id: record.form_id,
                  form_values: record.form_values,
                  // Add extracted fields for RelatedEntityItem compatibility
                  entity_name: recordName,
                  entity_description: recordDescription,
                  entity_thumbnail: recordThumbnail,
                  // Add reverse relationship metadata
                  _reverse_relationship: {
                    sourceFormName: reverseRel.sourceFormName,
                    relationshipKey: reverseRel.relationshipKey,
                    displayName: reverseRel.displayName,
                    recordLinkFieldLabel: reverseRel.recordLinkFieldLabel
                  }
                };
              })
          );

          console.log(`🔄 Successfully fetched ${transformedResults.length} full records from ${reverseRel.sourceFormName}`);
          allResults.push(...transformedResults);
        } else {
          console.log(`🔄 No ${reverseRel.sourceFormName} records found linking to ${targetRecordId}`);
        }
      } catch (relError: any) {
        console.warn(`🔄 Error querying ${reverseRel.sourceFormName}:`, relError);

        // Report progress even for failed queries
        if (onProgress) {
          const errorMessage = relError.message?.includes("timeout")
            ? `${reverseRel.sourceFormName} (timed out)`
            : `${reverseRel.sourceFormName} (failed)`;
          onProgress(errorMessage, i + 1, reverseRelationships.length);
        }

        // Continue with other relationships even if one fails
      }
    }

    console.log(`🔄 Reverse relationship query complete: found ${allResults.length} total records`);
    return allResults;
  } catch (error) {
    console.error("🔄 Error fetching reverse relationships:", error);
    throw error;
  }
};

/**
 * Check an entity whether it is Fulcrum photo or not
 * @param entity - An any object
 * @param endPoint - An api end point
 * @returns boolean
 * */
export function isFulcrumMedia(entity: any, endPoint?: ApiEndpoint | null) {
  const dataSourceType = getDataSourceType(entity, endPoint);

  return !!(
    // Direct Fulcrum photo indicators
    (
      entity?.fulcrum_photo?.photo_id ||
      entity?.fulcrum_data?.form_id ||
      entity?._source?.type === "fulcrum" ||
      // Media type indicators combined with Fulcrum data source
      (entity?.media_type === "photo" &&
        (entity?.dataSourceType === "fulcrum" || entity?.form_id || entity?.fulcrum_id || dataSourceType === "fulcrum")) ||
      // Photo name pattern from Fulcrum (like "Photo 1", "Photo 10")
      (entity?.name?.match(/^Photo \d+$/) &&
        (entity?.dataSourceType === "fulcrum" || endPoint?.dataSourceType === "fulcrum" || dataSourceType === "fulcrum"))
    )
  );
}
